import { Layers3 } from "lucide-react";
import { useMemo, useState } from "react";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { SearchInput } from "@/components/shared/search-input";
import { Skeleton } from "@/components/ui/skeleton";
import { useRoleMatrix } from "@/hooks/use-role-matrix";
import { cn } from "@/lib/utils";

/**
 * Role × Category overlap.
 *
 * The requirements matrix read a level up: columns are categories rather than
 * individual capabilities, because two roles both requiring something in the
 * same category is what makes a move between them an overlap rather than a
 * loss (Kori Deitz, 2026-10-05, on developer → AI engineer — at the time this
 * was a competency-only "theme" axis; it now spans skills too, since
 * categories and themes merged into one many-to-many concept on 2026-10-14).
 *
 * Its own view rather than a mode on `RoleCapabilityMatrix`: that grid carries
 * rotated headers, delegated column-hover painting and click-to-focus, all
 * sized for ~40 capability columns. A category axis is a handful of columns
 * wide and reads as an ordinary table, so sharing that machinery would cost
 * its complexity and buy nothing.
 *
 * **A blank row and an uncategorised role are different findings.** A role
 * whose capabilities nobody has filed yet is marked as such and greyed,
 * because reading it as "overlaps with nothing" is the one wrong conclusion
 * this screen could invite.
 */

function LoadingGrid() {
  return (
    <div className="space-y-2">
      <Skeleton className="h-9 w-72" />
      <Skeleton className="h-[320px] w-full rounded-lg" />
    </div>
  );
}

export function RoleCategoryMatrix() {
  const { data, isLoading, isError, error, refetch } = useRoleMatrix();
  const [roleSearch, setRoleSearch] = useState("");

  const countByCell = useMemo(
    () =>
      new Map((data?.categoryRequired ?? []).map((c) => [`${c.roleId}|${c.categoryId}`, c.count])),
    [data],
  );
  const coverageByRole = useMemo(
    () => new Map((data?.coverage ?? []).map((c) => [c.roleId, c])),
    [data],
  );

  const visibleRoles = useMemo(() => {
    if (!data) return [];
    const term = roleSearch.trim().toLowerCase();
    return data.roles.filter((r) => {
      const coverage = coverageByRole.get(r.id);
      const total = (coverage?.skills ?? 0) + (coverage?.competencies ?? 0);
      return (
        (term === "" || r.name.toLowerCase().includes(term)) &&
        // A role requiring nothing at all has no bearing on overlap and only
        // lengthens the table.
        total > 0
      );
    });
  }, [data, roleSearch, coverageByRole]);

  if (isLoading) return <LoadingGrid />;
  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load the category overlap"}
        onRetry={() => refetch()}
      />
    );
  }
  if (!data) return null;

  if (data.categories.length === 0) {
    return (
      <EmptyState
        icon={Layers3}
        title="No categorised capabilities yet"
        description="Once skills and competencies are filed under categories, this shows which roles share them. Categories are set up under Settings → Config → Categories."
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          value={roleSearch}
          onValueChange={setRoleSearch}
          placeholder="Search roles…"
          className="w-full sm:w-64"
        />
      </div>

      <div className="overflow-x-auto rounded-lg border contain-content">
        <table className="w-full border-collapse text-sm">
          <caption className="sr-only">
            Roles against capability categories. A number means the role requires that many skills
            or competencies carrying the category.
          </caption>
          <thead>
            <tr className="border-b bg-muted/50">
              <th
                scope="col"
                className="sticky left-0 z-10 bg-muted/50 px-4 py-2 text-left font-medium"
              >
                Role
              </th>
              {data.categories.map((cat) => (
                <th key={cat.id} scope="col" className="px-3 py-2 text-center font-medium">
                  {cat.name}
                </th>
              ))}
              <th scope="col" className="px-4 py-2 text-right font-medium whitespace-nowrap">
                Categorized
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleRoles.map((role) => {
              const coverage = coverageByRole.get(role.id);
              const categorized =
                (coverage?.categorizedSkills ?? 0) + (coverage?.categorizedCompetencies ?? 0);
              const total = (coverage?.skills ?? 0) + (coverage?.competencies ?? 0);
              // The distinction this screen exists to protect: nothing filed
              // yet is NOT the same as nothing in common.
              const incomplete = categorized === 0;

              return (
                <tr key={role.id} className="border-b last:border-0 hover:bg-muted/30">
                  <th
                    scope="row"
                    className={cn(
                      "sticky left-0 z-10 bg-background px-4 py-2 text-left font-medium",
                      incomplete && "text-muted-foreground",
                    )}
                  >
                    {role.name}
                    {role.status === "draft" && (
                      <span className="ml-2 text-2xs text-muted-foreground">Draft</span>
                    )}
                  </th>

                  {incomplete ? (
                    <td
                      colSpan={data.categories.length}
                      className="px-3 py-2 text-center text-2xs text-muted-foreground italic"
                    >
                      None of this role's {total} {total === 1 ? "capability" : "capabilities"} has
                      a category yet
                    </td>
                  ) : (
                    data.categories.map((cat) => {
                      const count = countByCell.get(`${role.id}|${cat.id}`) ?? 0;
                      return (
                        <td key={cat.id} className="px-3 py-2 text-center tabular-nums">
                          {count > 0 ? (
                            <span
                              className="inline-flex min-w-6 justify-center rounded bg-primary/10 px-1.5 py-0.5 font-medium text-primary"
                              title={`${role.name} requires ${count} ${
                                count === 1 ? "capability" : "capabilities"
                              } in ${cat.name}`}
                            >
                              {count}
                            </span>
                          ) : (
                            <>
                              <span aria-hidden="true" className="text-muted-foreground">
                                —
                              </span>
                              <span className="sr-only">None</span>
                            </>
                          )}
                        </td>
                      );
                    })
                  )}

                  <td
                    className={cn(
                      "px-4 py-2 text-right tabular-nums whitespace-nowrap",
                      incomplete ? "text-warning-foreground" : "text-muted-foreground",
                    )}
                  >
                    {categorized} of {total}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {visibleRoles.length === 0 && (
        <EmptyState
          icon={Layers3}
          title="No roles to show"
          description="No role matching that search requires any skills or competencies."
        />
      )}
    </div>
  );
}
